import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NO_FILTERS } from '../../domain/plants/plantFilters';

/**
 * What the browse screen was showing when you left it (SPEC-011 D-8).
 *
 * Opening a plant unmounts `PlantsScreen`, and coming back mounts a fresh one.
 * Without this, the back button hands a gardener the top of an unfiltered
 * catalogue every time — which for a screen whose whole job is browsing is the
 * difference between a shelf and a search box.
 *
 * A module-level record rather than Web Storage. This is view state for one
 * screen inside one SPA session: it never needs to survive a reload, and the
 * moment it is persisted it becomes something that can be stale against a
 * catalogue that changed under it. `useGarden` reaches for storage because a
 * garden belongs to the user; where they had scrolled to does not.
 */

const remembered = {
    query: '',
    filters: NO_FILTERS,
    scrollTop: 0,
    railOffsets: {},
};

/**
 * The app scrolls in `.app-shell__content`, not the window — the same container
 * `useScrollReset` and `useParallaxScene` read, looked up the same way.
 */
function scrollContainer() {
    return typeof document === 'undefined' ? null : document.querySelector('.app-shell__content');
}

/** The query and the filters, resumed from wherever the last visit left them. */
export function useBrowseState() {
    const [query, setQuery] = useState(remembered.query);
    const [filters, setFilters] = useState(remembered.filters);

    useEffect(() => {
        remembered.query = query;
        remembered.filters = filters;
    }, [query, filters]);

    return { query, setQuery, filters, setFilters };
}

/**
 * Put the page back where it was, once there is something to scroll.
 *
 * Restoring is a layout effect so the jump happens before the browser paints; a
 * plain `useEffect` shows the top of the catalogue for one frame first, which
 * reads as the page having reset and then thought better of it. It runs once per
 * mount — after that the position belongs to the user, not to this record.
 *
 * Recording listens, and its cleanup is a layout effect rather than a passive
 * one. The container this listens to is NOT unmounted with the screen: opening a
 * plant empties it and `useScrollReset` sends it to zero, so a listener that
 * outlives the commit records that zero over the position worth keeping. A
 * layout cleanup has already detached by then; a passive one is deferred past
 * paint and may not have.
 */
export function useRememberedScroll(ready) {
    const restored = useRef(false);

    useLayoutEffect(() => {
        const container = scrollContainer();

        if (!ready || restored.current || !container) {
            return;
        }

        restored.current = true;
        container.scrollTop = remembered.scrollTop;
    }, [ready]);

    useLayoutEffect(() => {
        const container = scrollContainer();

        if (!container) {
            return undefined;
        }

        const record = () => {
            remembered.scrollTop = container.scrollTop;
        };

        container.addEventListener('scroll', record, { passive: true });

        return () => container.removeEventListener('scroll', record);
    }, []);
}

/**
 * How far along each shelf was pushed. Kept per category rather than per render:
 * a shelf keeps its place across a filter change too, which is what stops the
 * whole page snapping back to the left every time a chip is tapped.
 */
export function rememberRailOffset(categoryKey, offset) {
    remembered.railOffsets[categoryKey] = offset;
}

export function recallRailOffset(categoryKey) {
    return remembered.railOffsets[categoryKey] || 0;
}
